import { useEffect, useRef } from "react";
import { Container, Page, Card } from "@project-mgmt/ui";
import { FileManager } from "@project-mgmt/feature-files";
import { useParams } from "react-router-dom";
import { useWorkspaceDetails } from "@project-mgmt/feature-workspaces";
import { useProject } from "@project-mgmt/feature-projects";
import { useTask } from "@project-mgmt/feature-tasks";
import { useFilePermissions } from "../hooks/useSimplePermissions";
import { notify } from "../providers/ToastProvider";
import type { TaskRouteParams } from "../types/routes";

export function FilesPage() {
  // Render tracking
  const renderCount = useRef(0);
  useEffect(() => {
    renderCount.current += 1;
    console.log(`[RENDER TRACKING] FilesPage rendered (count: ${renderCount.current})`, {
      timestamp: new Date().toISOString(),
    });
  });

  const { workspaceId, projectId, taskId } = useParams() as TaskRouteParams;

  // Fetch data with SWR hooks
  const { data: workspaceData, isLoading: workspaceLoading } = useWorkspaceDetails(workspaceId ?? "");
  const { data: projectData, isLoading: projectLoading } = useProject(projectId ?? "", workspaceId ?? "");
  const { data: taskData, isLoading: taskLoading } = useTask(taskId ?? "", workspaceId ?? "");

  // Extract entities from SWR responses
  const currentWorkspace = workspaceData?.workspace;
  const currentProject = projectData;
  const currentTask = taskData;

  const { canUploadFiles, canViewFiles } = useFilePermissions();

  // Show loading state while data is being fetched
  const isLoading = workspaceLoading || projectLoading || taskLoading;

  const handleFileUpload = () => {
    notify.success("File uploaded successfully");
  };

  const handleFileDownload = () => {
    notify.success("Download started");
  };

  const handleFileDelete = () => {
    notify.success("File deleted successfully");
  };

  return (
    // <NavigationGuard requireWorkspace requireProject requireTask>
    <Container size="xl">
      <Page>
        {isLoading ? (
          <Card className="p-4 sm:p-6">
            <div className="py-8 text-center">
              <div className="mb-4 text-4xl">⏳</div>
              <h3 className="text-textPrimary mb-2 text-lg font-medium">Loading...</h3>
              <p className="text-textSecondary">Fetching task details and files</p>
            </div>
          </Card>
        ) : (
          <Card className="p-4 sm:p-6">
          <div className="mb-6">
            <h1 className="text-textPrimary mb-2 text-xl font-bold sm:text-2xl">Task Files</h1>
            <p className="text-textSecondary text-sm">
              Managing files for task:{" "}
              <span className="text-textPrimary block truncate font-medium sm:inline">{currentTask?.title}</span>
            </p>
            <p className="text-textSecondary mt-1 truncate text-xs">
              Project: {currentProject?.name} • Workspace: {currentWorkspace?.name}
            </p>
          </div>

          {/* Check if user has permission to view files */}
          {canViewFiles && taskId ? (
            <FileManager
              workspaceId={workspaceId!}
              taskId={taskId}
              onFileUpload={handleFileUpload}
              onFileDownload={handleFileDownload}
              onFileDelete={handleFileDelete}
              showUpload={canUploadFiles}
            />
          ) : !taskId ? (
            <div className="py-8 text-center">
              <div className="mb-4 text-4xl">⚠️</div>
              <h3 className="text-textPrimary mb-2 text-lg font-medium">Task Not Found</h3>
              <p className="text-textSecondary">Unable to load files. Task ID is missing.</p>
            </div>
          ) : (
            <div className="py-8 text-center">
              <div className="mb-4 text-4xl">🔒</div>
              <h3 className="text-textPrimary mb-2 text-lg font-medium">Access Restricted</h3>
              <p className="text-textSecondary">You don't have permission to view files for this task.</p>
            </div>
          )}
          </Card>
        )}
      </Page>
    </Container>
    // </NavigationGuard>
  );
}
